import { type ReactElement, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import type { ApiError, OpsChatList, OpsChatMessage, OpsChatRecipient, OpsQuickReplies, OpsRideThread } from '@vello/api-client';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, EmptyState, Field, Notice, PageHeader, Panel, Select, TextArea, SectionTitle, Cluster, CapWidth, Stack, Text, ChatBubble, PreWrap, Split } from '@vello/web-theme';
import {
  chatRecipients,
  formatMarketInstant,
  isReplyToCentral,
  marketTimeZone,
  rideChatAuthority,
  sortThreads,
  tailOf,
} from '../../src/index';
import { useAdmin } from '../session';
import { VoiceMessagePlayer } from '../components/voice-message';
import { useRouter } from '../router';
import { PersonName } from '../components/person-name';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { LoadingBlock } from '../components/loading-block';

/**
 * Operações › Conversas das corridas (W9-CENTRAL-NEW-ORDER-CHAT, D473, ADR-0091; DevMobility H-001, H-003, H-007, I-081): the
 * central speaks inside a ride. On the left the market's live conversations — the ones a rider or a driver wrote TO the central
 * first — and on the right the whole thread of the one picked: what the rider and the driver said to each other and to the central,
 * each by role (never by account), with the operator who wrote for the central. The composer writes to the rider, the driver or
 * both (the driver only once one is assigned) — free text, or one of the market's governed ready messages in the Admin's language.
 *
 * The server authorizes every call at the ride's market and decides who reads what; a closed ride keeps its history and loses its
 * composer. One primary action: send. Neutral: no country or language is named; instants render in the market's IANA zone.
 */
const MAX_BODY = 500;

export function RideChatsScreen(): ReactElement {
  const { runtime, markets, grants } = useAdmin();
  const { params, navigate } = useRouter();
  const { t, lang } = useT();
  const { v, fail, explain } = useVocab();
  const authority = rideChatAuthority(grants);

  const { market, choices } = useScopeMarket();
  const timeZone = marketTimeZone(market, markets);

  const [list, setList] = useState<OpsChatList | undefined>(undefined);
  const [selected, setSelected] = useState<string | undefined>(params.ride);
  // Switching the header scope to another market drops the open conversation (it belongs to the previous market).
  const lastMarket = useRef(market);
  useEffect(() => {
    if (lastMarket.current !== market) {
      lastMarket.current = market;
      setSelected(undefined);
    }
  }, [market]);
  const [thread, setThread] = useState<OpsRideThread | undefined>(undefined);
  const [replies, setReplies] = useState<OpsQuickReplies | undefined>(undefined);
  const [to, setTo] = useState<OpsChatRecipient>('rider');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const listSeq = useRef(0);
  const threadSeq = useRef(0);

  const refuse = useCallback(
    (error: ApiError): string => explain(error) ?? t('lmap.err.generic', { code: fail(error) }),
    [t, fail, explain],
  );

  const loadList = useCallback(async (): Promise<void> => {
    const seq = ++listSeq.current;
    if (market === undefined) {
      setList(undefined);
      return;
    }
    const res = await runtime.centralDesk.rideChats(market);
    if (seq !== listSeq.current) return;
    if (res.ok) {
      setList(res.value);
      setError(undefined);
    } else setError(refuse(res.error));
  }, [runtime, market, refuse]);

  const loadThread = useCallback(async (): Promise<void> => {
    const seq = ++threadSeq.current;
    if (selected === undefined) {
      setThread(undefined);
      return;
    }
    const res = await runtime.centralDesk.rideThread(selected);
    if (seq !== threadSeq.current) return;
    if (res.ok) setThread(res.value);
    else {
      setThread(undefined);
      setNotice({ tone: 'danger', text: refuse(res.error) });
    }
  }, [runtime, selected, refuse]);

  useEffect(() => {
    setList(undefined);
    void loadList();
  }, [loadList]);

  useEffect(() => {
    setThread(undefined);
    void loadThread();
  }, [loadThread]);

  // The ready messages of the market, in the Admin's language (an empty list when the market governs none).
  useEffect(() => {
    if (market === undefined || !authority.maySend) {
      setReplies(undefined);
      return;
    }
    let live = true;
    void runtime.centralDesk.rideChatQuickReplies(market, lang).then((r) => {
      if (live) setReplies(r.ok ? r.value : undefined);
    });
    return () => {
      live = false;
    };
  }, [runtime, market, lang, authority.maySend]);

  const reloadBoth = useCallback(async (): Promise<void> => {
    await Promise.all([loadList(), loadThread()]);
  }, [loadList, loadThread]);
  useAutoRefresh(reloadBoth, { intervalMs: AUTO_REFRESH_MS.live, enabled: market !== undefined });

  const recipients = chatRecipients(thread?.chat.hasDriver === true);
  useEffect(() => {
    if (!recipients.includes(to)) setTo('rider');
  }, [recipients, to]);

  const send = async (input: { readonly body?: string; readonly replyCode?: string }): Promise<void> => {
    if (selected === undefined) return;
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.centralDesk.sendRideMessage(selected, { to, ...input, ...(input.replyCode !== undefined ? { language: lang } : {}) });
    setBusy(false);
    if (res.ok) {
      setBody('');
      await Promise.all([loadThread(), loadList()]);
    } else setNotice({ tone: 'danger', text: refuse(res.error) });
  };

  const speaker = (m: OpsChatMessage): ReactNode => (m.senderRole === 'operator' ? <>{v('central_desk.chat_sender_role', 'operator')} · <PersonName id={m.operatorId} /></> : v('central_desk.chat_sender_role', m.senderRole));
  const audience = (m: OpsChatMessage): string | undefined => (m.recipient !== undefined ? v('central_desk.chat_recipient', m.recipient) : undefined);
  const canWrite = authority.maySend && thread?.chat.open === true;

  const threads = sortThreads(list?.threads ?? []);

  return (
    <>
      <PageHeader
        title={t('cdesk.chats.title')}
        subtitle={t('cdesk.chats.subtitle')}
      />

      {error !== undefined && <Notice tone="danger">{error}</Notice>}
      {notice !== undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}

      {market === undefined ? (
        <Panel>
          <ScopeMarketNotice choices={choices} />
        </Panel>
      ) : (
        <Split ratio="1-2" gap={5}>
          <Panel>
            <Stack gap={4}>
              <SectionTitle>
                {t('cdesk.chats.live')}
                {(list?.awaiting ?? 0) > 0 && <> <Badge tone="warning">{t('cdesk.chats.awaitingCount', { value: list?.awaiting ?? 0 })}</Badge></>}
              </SectionTitle>
              {list === undefined ? (
                error !== undefined ? <EmptyState title={t('cdesk.chats.failed')} /> : <LoadingBlock />
              ) : threads.length === 0 ? (
                <EmptyState title={t('cdesk.chats.empty')} detail={t('cdesk.chats.emptyDetail')} />
              ) : (
                <Stack as="ul" gap={2}>
                  {threads.map((th) => (
                    <li key={th.rideId}>
                      <Button
                        variant={th.rideId === selected ? 'secondary' : 'ghost'}
                        aria-label={t('cdesk.chats.open', { id: tailOf(th.rideId) })}
                        aria-pressed={th.rideId === selected}
                        onClick={() => { setSelected(th.rideId); setNotice(undefined); }}
                      >
                        <span className="tnum">{tailOf(th.rideId)}</span>
                        {' · '}
                        <Text role="caption" tone="secondary" mono>{formatMarketInstant(th.lastMessageAt, timeZone, lang, 'datetime')}</Text>
                        {th.awaitingOperator && <> <Badge tone="warning">{t('cdesk.chats.awaiting')}</Badge></>}
                      </Button>
                    </li>
                  ))}
                </Stack>
              )}
            </Stack>
          </Panel>

          <Panel>
            <Stack gap={4}>
              {selected === undefined ? (
                <EmptyState title={t('cdesk.thread.pick')} detail={t('cdesk.thread.pickDetail')} />
              ) : thread === undefined ? (
                <LoadingBlock />
              ) : (
                <Stack gap={4}>
                  <Cluster gap={3} justify="between" wrap>
                    <Text as="h2" role="title">{t('cdesk.thread.title', { id: tailOf(thread.rideId) })}</Text>
                    <Button variant="ghost" onClick={() => navigate('ride_detail', { id: thread.rideId })}>{t('cdesk.thread.openRide')}</Button>
                  </Cluster>
                  {!thread.chat.open && <Notice tone="neutral">{t('cdesk.thread.closed')}</Notice>}
                  {thread.messages.length === 0 ? (
                    <EmptyState title={t('cdesk.thread.empty')} />
                  ) : (
                    <Stack as="ol" gap={3} aria-label={t('cdesk.thread.history')}>
                      {thread.messages.map((m) => (
                        <ChatBubble key={m.id} data-testid={`chat-message-${m.senderRole}`} side={m.senderRole === 'operator' ? 'end' : 'start'}>
                          <Text as="div" role="caption" tone="secondary">
                            <strong>{speaker(m)}</strong>
                            {audience(m) !== undefined && <> → {audience(m)}</>}
                            {isReplyToCentral(m) && <> <Badge tone="warning">{t('cdesk.thread.replyToCentral')}</Badge></>}
                            {m.quickReply !== undefined && <> <Badge tone="neutral">{t('cdesk.thread.ready')}</Badge></>}
                            {m.kind === 'audio' && <> <Badge tone="neutral">{t('cdesk.thread.voice')}</Badge></>}
                          </Text>
                          {m.kind === 'audio' && m.audio !== undefined ? (
                            <VoiceMessagePlayer rideId={thread.rideId} messageId={m.id} durationSeconds={m.audio.durationSeconds} />
                          ) : (
                            <PreWrap>{m.body}</PreWrap>
                          )}
                          <Text as="div" role="caption" tone="secondary" mono>{formatMarketInstant(m.createdAt, timeZone, lang, 'datetime-seconds')}{m.moderated ? ` · ${t('cdesk.thread.moderated')}` : ''}</Text>
                        </ChatBubble>
                      ))}
                    </Stack>
                  )}

                  {canWrite ? (
                    <Stack gap={3}>
                      <CapWidth size="sm">
                        <Field label={t('cdesk.composer.to')} htmlFor="chat-to">
                          <Select id="chat-to" value={to} onChange={(e) => setTo(e.target.value as OpsChatRecipient)}>
                            {recipients.map((r) => (
                              <option key={r} value={r}>{v('central_desk.chat_recipient', r)}</option>
                            ))}
                          </Select>
                        </Field>
                      </CapWidth>
                      {replies !== undefined && replies.replies.length > 0 && (
                        <Cluster gap={2} wrap role="group" aria-label={t('cdesk.composer.ready')}>
                          {replies.replies.map((r) => (
                            <Button key={r.code} variant="secondary" disabled={busy} aria-label={t('cdesk.composer.sendReady', { text: r.text })} onClick={() => void send({ replyCode: r.code })}>{r.text}</Button>
                          ))}
                        </Cluster>
                      )}
                      <Field label={t('cdesk.composer.message')} htmlFor="chat-body" hint={t('cdesk.composer.hint', { max: thread.chat.maxBodyLength || MAX_BODY })}>
                        <TextArea id="chat-body" value={body} maxLength={thread.chat.maxBodyLength || MAX_BODY} onChange={(e) => setBody(e.target.value)} />
                      </Field>
                      <Button variant="primary" disabled={busy || body.trim() === ''} onClick={() => void send({ body: body.trim() })}>{t('cdesk.composer.send')}</Button>
                    </Stack>
                  ) : !authority.maySend ? (
                    <Notice tone="neutral">{t('cdesk.composer.readOnly')}</Notice>
                  ) : null}
                </Stack>
              )}
            </Stack>
          </Panel>
        </Split>
      )}
    </>
  );
}
